/**
 * DeletedWorkStoppagesModal
 *
 * The arrêts deleted from the suivi, read back from the audit trail. Deleting an arrêt removes
 * it and its reimbursements for good, and it drops out of the table — this is where the
 * "traçabilité conservée" of the delete dialog can actually be read.
 *
 * Each row is the arrêt as it stood when deleted (the API's DELETE snapshot) with the collab,
 * motif and period resolved. Expanding it shows the arrêt's fields, the reimbursements removed
 * with it, and its full trail, fetched on first expand.
 *
 * Read-only: nothing here restores or edits anything.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Chip, Collapse, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import Langs, { Localize } from "../../app/lang/langs";
import {
  getDeletedWorkStoppages, getDeletedWorkStoppageHistory,
} from "../../components/workStoppages/workStoppagesSlice";
import { StatusChip } from "../../components/workStoppages/WorkStoppageStatus";
import WorkStoppageHistoryTable, {
  NAVY, formatDate, formatAmount, tableFrameSx,
} from "../../components/workStoppages/WorkStoppageHistoryTable";
import FormDialog, { FormSection, FormButton, formFieldSx } from "../../components/popups/FormDialog";

const yesNo = (value) =>
  value === null || typeof value === "undefined" ? "—" : Localize(Number(value) ? "YES" : "NO");

/** One label/value pair of the expanded arrêt. */
function Field({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontWeight: 600, fontSize: "0.75rem", color: NAVY, mb: 0.25 }}>{label}</Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
        {children === null || typeof children === "undefined" || children === "" ? "—" : children}
      </Typography>
    </Box>
  );
}

function DeletedPayments({ payments }) {
  return (
    <Box sx={tableFrameSx}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{Localize("WORK_STOPPAGE_PAYMENT_SOURCE")}</TableCell>
            <TableCell>{Localize("WORK_STOPPAGE_PAYMENT_DATE")}</TableCell>
            <TableCell>{Localize("WORK_STOPPAGE_PERIOD_FROM")}</TableCell>
            <TableCell>{Localize("WORK_STOPPAGE_PERIOD_TO")}</TableCell>
            <TableCell>{Localize("WORK_STOPPAGE_AMOUNT")}</TableCell>
            <TableCell>{Localize("Comments")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(payments || []).map((payment) => (
            <TableRow key={payment.id}>
              <TableCell>
                <Chip
                  size="small"
                  label={payment.source === "PREVOYANCE" ? Localize("WORK_STOPPAGE_PREVOYANCE") : "CPAM"}
                  color={payment.source === "PREVOYANCE" ? "info" : "success"}
                  sx={{ fontWeight: 600, borderRadius: "8px" }}
                />
              </TableCell>
              <TableCell>{formatDate(payment.payment_date)}</TableCell>
              <TableCell>{formatDate(payment.period_from)}</TableCell>
              <TableCell>{formatDate(payment.period_to)}</TableCell>
              <TableCell>{formatAmount(payment.amount)}</TableCell>
              <TableCell>{payment.comments || "—"}</TableCell>
            </TableRow>
          ))}
          {!(payments || []).length && (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.6 }}>
                  <Langs str="WORK_STOPPAGE_NO_PAYMENTS" />
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

/** The expanded part of a row: the arrêt as it was, what went with it, and its trail. */
function DeletedWorkStoppageDetails({ row, history, reference }) {
  const ws = row.stoppage || {};
  return (
    <Box sx={{ py: 1.5 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
          gap: 1.5,
          px: 1,
          pb: 1,
        }}
      >
        <Field label={Localize("WORK_STOPPAGE_STATUS")}>
          <StatusChip status={ws.stoppage_status === "PROLONGATION" ? "PROLONGATION" : "INITIAL"} />
        </Field>
        <Field label={Localize("WORK_STOPPAGE_RECEIVED_AT")}>{formatDate(ws.received_at)}</Field>
        <Field label={Localize("WORK_STOPPAGE_DECLARED_AT")}>{formatDate(ws.declared_at)}</Field>
        <Field label={Localize("WORK_STOPPAGE_SUBROGATION")}>{yesNo(ws.subrogation)}</Field>
        <Field label={Localize("WORK_STOPPAGE_SALARY_MAINTENANCE")}>{yesNo(ws.salary_maintenance)}</Field>
        <Field label={Localize("WORK_STOPPAGE_CARENCE_DAYS")}>{ws.carence_days}</Field>
        <Field label={Localize("WORK_STOPPAGE_CPAM_DAYS")}>{ws.cpam_days}</Field>
        <Field label={Localize("WORK_STOPPAGE_THERAPEUTIC_RATE")}>
          {ws.therapeutic_rate === null || typeof ws.therapeutic_rate === "undefined" ? "—" : `${Number(ws.therapeutic_rate)} %`}
        </Field>
        <Field label={Localize("WORK_STOPPAGE_CPAM_REIMBURSEMENT_STATUS")}>
          {ws.cpam_reimbursement_status ? <StatusChip status={ws.cpam_reimbursement_status} /> : null}
        </Field>
        <Field label={Localize("WORK_STOPPAGE_PREVOYANCE_REIMBURSEMENT_STATUS")}>
          {ws.prevoyance_reimbursement_status ? <StatusChip status={ws.prevoyance_reimbursement_status} /> : null}
        </Field>
        <Field label={Localize("Comments")}>{ws.comments}</Field>
      </Box>

      <FormSection><Langs str="WORK_STOPPAGE_DELETED_PAYMENTS" /></FormSection>
      <DeletedPayments payments={row.payments} />

      <FormSection><Langs str="WORK_STOPPAGE_HISTORY" /></FormSection>
      <WorkStoppageHistoryTable history={history} reference={reference} />
    </Box>
  );
}

/**
 * @param {Object} props
 * @param {boolean} props.visible
 * @param {Function} props.onHide
 */
export default function DeletedWorkStoppagesModal({ visible, onHide }) {
  const dispatch = useDispatch();
  const isMobile = useSelector((state) => state.settings.isMobile);
  const list = useSelector((state) => state.workStoppages.deletedList);
  const loading = useSelector((state) => state.workStoppages.loadingDeletedList);
  const historyById = useSelector((state) => state.workStoppages.deletedHistory);
  const reference = useSelector((state) => state.workStoppages.reference);

  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");

  // Re-fetched on every open: an arrêt deleted a minute ago must be there.
  useEffect(() => {
    if (visible) dispatch(getDeletedWorkStoppages());
    if (!visible) {
      setExpanded(null);
      setSearch("");
    }
  }, [visible, dispatch]);

  const toggle = (row) => {
    const next = expanded === row.id ? null : row.id;
    setExpanded(next);
    if (next !== null && !historyById[row.id_work_stoppage])
      dispatch(getDeletedWorkStoppageHistory(row.id_work_stoppage));
  };

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return list || [];
    return (list || []).filter((row) =>
      [row.prenom, row.nom, row.motif, row.deletedBy, row.id_work_stoppage]
        .some((value) => String(value ?? "").toLowerCase().includes(needle))
    );
  }, [list, search]);

  const COLUMNS = 9;

  return (
    <FormDialog
      open={visible}
      onClose={onHide}
      title={<Langs str="WORK_STOPPAGES_DELETED_TITLE" />}
      maxWidth="lg"
      fullScreen={isMobile === 2}
      actions={<FormButton variant="secondary" onClick={onHide}><Langs str="CLOSE" /></FormButton>}
    >
      <Typography variant="body2" color="text.secondary" sx={{ px: 1, pb: 1 }}>
        <Langs str="WORK_STOPPAGES_DELETED_HELP" />
      </Typography>
      <Box sx={{ px: 1, pb: 1.5 }}>
        <TextField
          size="small"
          fullWidth
          sx={formFieldSx}
          placeholder={Localize("Search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Box>

      <Box sx={tableFrameSx}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={48} />
              <TableCell>{Localize("WORK_STOPPAGE_DELETED_AT")}</TableCell>
              <TableCell>{Localize("WORK_STOPPAGE_DELETED_BY")}</TableCell>
              <TableCell>{Localize("WORK_STOPPAGE_COLLAB")}</TableCell>
              <TableCell>{Localize("WORK_STOPPAGE_MOTIF")}</TableCell>
              <TableCell>{Localize("START_DATE")}</TableCell>
              <TableCell>{Localize("END_DATE")}</TableCell>
              <TableCell align="right">{Localize("WORK_STOPPAGE_CPAM_TOTAL")}</TableCell>
              <TableCell align="right">{Localize("WORK_STOPPAGE_PREVOYANCE_TOTAL")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const open = expanded === row.id;
              return (
                <React.Fragment key={row.id}>
                  <TableRow hover onClick={() => toggle(row)} sx={{ cursor: "pointer", "& > td": open ? { borderBottom: "none" } : {} }}>
                    <TableCell>
                      <IconButton size="small">
                        {open ? <KeyboardArrowUpRoundedIcon fontSize="small" /> : <KeyboardArrowDownRoundedIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{moment(row.deleted_at).format("DD/MM/YYYY HH:mm")}</TableCell>
                    <TableCell>{row.deletedBy || "—"}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {row.prenom || row.nom ? `${row.prenom || ""} ${row.nom || ""}`.trim() : `#${row.stoppage?.id_collab ?? "—"}`}
                    </TableCell>
                    <TableCell>{row.motif || "—"}</TableCell>
                    <TableCell>{formatDate(row.start_date)}</TableCell>
                    <TableCell>{formatDate(row.end_date)}</TableCell>
                    <TableCell align="right">{formatAmount(row.cpam_total)}</TableCell>
                    <TableCell align="right">{formatAmount(row.prevoyance_total)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={COLUMNS} sx={{ py: 0, ...(open ? {} : { borderBottom: "none" }) }}>
                      <Collapse in={open} timeout="auto" unmountOnExit>
                        <DeletedWorkStoppageDetails
                          row={row}
                          history={historyById[row.id_work_stoppage]}
                          reference={reference}
                        />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={COLUMNS}>
                  <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.6 }}>
                    <Langs str={loading ? "LOADING" : "WORK_STOPPAGES_DELETED_NONE"} />
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </FormDialog>
  );
}
